import { useEffect, useId, useRef, useState } from 'react';
import { ChevronDown, History, Moon, Plus, Settings2, Sun } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface LearnMenuProps {
  theme: 'light' | 'dark';
  onNewChat: () => void;
  onToggleTheme: () => void;
  busy: boolean;
  context: string;
}

export default function LearnMenu({ theme, onNewChat, onToggleTheme, busy, context }: LearnMenuProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const id = useId();
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    const closeOutside = (event: PointerEvent) => {
      if (!ref.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', closeOutside);
    return () => document.removeEventListener('pointerdown', closeOutside);
  }, [open]);

  const act = (action: () => void) => {
    setOpen(false);
    action();
    triggerRef.current?.focus();
  };

  return (
    <div ref={ref} className="relative" onBlur={(event) => {
      if (!event.currentTarget.contains(event.relatedTarget as Node)) setOpen(false);
    }} onKeyDown={(event) => {
      if (event.key === 'Escape') { setOpen(false); triggerRef.current?.focus(); }
    }}>
      <button ref={triggerRef} type="button" className="tutor-learn-pill" aria-label="Learn — chat options" aria-expanded={open} aria-controls={id} onClick={() => setOpen(value => !value)}>
        Learn <ChevronDown size={14} aria-hidden="true" />
      </button>
      {open && <div id={id} className="tutor-learn-menu" aria-label="Chat options">
        <p className="tutor-menu-context">{context}</p>
        <button type="button" disabled={busy} onClick={() => act(onNewChat)}><Plus size={17} aria-hidden="true" />New chat</button>
        <button type="button" onClick={() => act(() => navigate('/history'))}><History size={17} aria-hidden="true" />Chat history</button>
        <button type="button" onClick={() => act(onToggleTheme)}>{theme === 'dark' ? <Sun size={17} aria-hidden="true" /> : <Moon size={17} aria-hidden="true" />}{theme === 'dark' ? 'Use light theme' : 'Use dark theme'}</button>
        <button type="button" onClick={() => act(() => navigate('/profile'))}><Settings2 size={17} aria-hidden="true" />Learning preferences</button>
      </div>}
    </div>
  );
}
